<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RoutePlan } from '@/api/route-plans';

const { t } = useI18n();

interface Props {
  status: RoutePlan['status'];
}

const props = defineProps<Props>();

type StepState = 'done' | 'active' | 'future';

const STATES_BY_STATUS: Record<RoutePlan['status'], StepState[]> = {
  draft: ['active', 'future', 'future'],
  processing: ['done', 'active', 'future'],
  completed: ['done', 'active', 'future'],
  failed: ['done', 'active', 'future'],
  applied: ['done', 'done', 'done'],
};

const STEP_COLORS: Record<StepState, string> = {
  done: 'success',
  active: 'primary',
  future: 'grey',
};

const STEP_TEXT_CLASSES: Record<StepState, string> = {
  done: 'text-success',
  active: 'text-primary',
  future: 'text-medium-emphasis',
};

const STEP_KEYS = ['configure', 'review', 'apply'] as const;

const steps = computed(() => {
  const states = STATES_BY_STATUS[props.status];

  return STEP_KEYS.map((key, index) => {
    const state = states[index] || 'future';

    return {
      key,
      label: t(`routePlan.steps.${key}`),
      state,
      color: STEP_COLORS[state],
      textClass: STEP_TEXT_CLASSES[state],
      showSpinner: props.status === 'processing' && key === 'review',
    };
  });
});
</script>

<template>
  <div class="d-flex align-center justify-center flex-wrap ga-1 py-2">
    <template
      v-for="(step, index) in steps"
      :key="step.key"
    >
      <v-icon
        v-if="index > 0"
        class="mx-2 text-medium-emphasis"
      >
        mdi-chevron-right
      </v-icon>
      <div
        class="d-flex align-center"
        :class="[`step-${step.state}`, step.textClass]"
      >
        <v-avatar
          size="32"
          :color="step.color"
          class="mr-2"
        >
          <v-icon v-if="step.state === 'done'">
            mdi-check
          </v-icon>
          <span
            v-else
            class="text-body-2 font-weight-bold"
          >
            {{ index + 1 }}
          </span>
        </v-avatar>
        <span class="text-subtitle-1 font-weight-medium">
          {{ step.label }}
        </span>
        <v-progress-circular
          v-if="step.showSpinner"
          indeterminate
          size="16"
          width="2"
          color="primary"
          class="ml-2"
        />
      </div>
    </template>
  </div>
</template>
